import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Sidebar } from "@/components/Sidebar";
import { TopBar } from "@/components/TopBar";
import { Login } from "@/components/Login";
import { AcceptInvite } from "@/components/AcceptInvite";
import Home from "@/pages/Home";
import ConversacionesPage from "@/pages/ConversacionesPage";
import CitasPage from "@/pages/CitasPage";
import LlamadasPage from "@/pages/LlamadasPage";
import ConectarCuentaPage from "@/pages/ConectarCuentaPage";
import ConocimientoPage from "@/pages/ConocimientoPage";
import MetricasPage from "@/pages/MetricasPage";
import EntrenarPage from "@/pages/EntrenarPage";
import RespuestasAutomaticasPage from "@/pages/RespuestasAutomaticasPage";
import ConfiguracionPage from "@/pages/ConfiguracionPage";
import { getScheduleStatus, authMe, getStoredToken, setAuthToken } from "@/lib/dentiumApi";

const TITLES = {
  "/": "Inicio",
  "/conversaciones": "Conversaciones",
  "/citas": "Citas",
  "/llamadas": "Llamadas",
  "/metricas": "Métricas",
  "/conectar": "Conectar cuenta",
  "/conocimiento": "Base de conocimientos",
  "/configuracion": "Configuración",
  "/entrenar": "Entrenar asistente",
  "/respuestas-automaticas": "Respuestas automáticas",
};

function DashboardLayout({ user, onLogout }) {
  const location = useLocation();
  const [scheduleMode, setScheduleMode] = useState("auto");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const status = p.get("connect");
    if (!status) return;
    const canal = p.get("canal") === "instagram" ? "Instagram" : "WhatsApp";
    if (status === "ok") {
      toast.success(`${canal} conectado`, { description: "Cuenta oficial autorizada correctamente a través de Meta." });
    } else {
      toast.error(`No se conectó ${canal}`, { description: "No se pudo completar la autorización en Meta." });
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const { data: sched } = useQuery({
    queryKey: ["schedule", scheduleMode],
    queryFn: () => getScheduleStatus(scheduleMode),
    refetchInterval: 30000,
  });
  const inWindow = sched?.in_window ?? false;
  const title = TITLES[location.pathname] || "Panel";
  const isAdmin = (user?.role || "admin") === "admin";

  return (
    <div className="flex h-screen bg-[#FAF8F5] overflow-hidden">
      <Toaster position="bottom-right" richColors />
      <Sidebar isAdmin={isAdmin} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          title={title}
          isAdmin={isAdmin}
          scheduleMode={scheduleMode}
          setScheduleMode={setScheduleMode}
          inWindow={inWindow}
          user={user}
          onLogout={onLogout}
        />
        <main className="flex-1 min-h-0 overflow-hidden">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/conversaciones" element={<ConversacionesPage />} />
            <Route path="/citas" element={<CitasPage />} />
            <Route path="/llamadas" element={<LlamadasPage />} />
            <Route path="/metricas" element={<MetricasPage />} />
            <Route path="/conectar" element={<ConectarCuentaPage />} />
            <Route path="/conocimiento" element={<ConocimientoPage />} />
            <Route path="/entrenar" element={<EntrenarPage />} />
            <Route path="/respuestas-automaticas" element={isAdmin ? <RespuestasAutomaticasPage /> : <Navigate to="/" replace />} />
            <Route path="/configuracion" element={isAdmin ? <ConfiguracionPage user={user} /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null); // null = comprobando, false = sin sesión, obj = autenticado

  useEffect(() => {
    if (window.location.pathname === "/invitacion") {
      setAuthToken(null);
      setUser(false);
      return;
    }
    if (!getStoredToken()) {
      setUser(false);
      return;
    }
    authMe()
      .then((u) => setUser(u))
      .catch(() => {
        setAuthToken(null);
        setUser(false);
      });
  }, []);

  const handleLogout = () => {
    setAuthToken(null);
    setUser(false);
  };

  if (window.location.pathname === "/invitacion" && !user) {
    return (
      <>
        <Toaster position="bottom-right" richColors />
        <AcceptInvite onAuthed={setUser} />
      </>
    );
  }

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-white animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Toaster position="bottom-right" richColors />
        <Login onAuthed={setUser} />
      </>
    );
  }

  return (
    <BrowserRouter>
      <DashboardLayout user={user} onLogout={handleLogout} />
    </BrowserRouter>
  );
}

export default App;
